import { useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";

// @ts-ignore
import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import type { DTO } from "@equidefi/shared/types/dto";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z.object({
  first_name: z
    .string()
    .trim()
    .min(1, MESSAGE.REQUIRED("First name"))
    .min(2, "First name must be at least 2 characters")
    .regex(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY),

  middle_name: z
    .string()
    .nullable()
    .refine((value) => !value || ASCII_ONLY_REGEX.test(value.trim()), {
      message: MESSAGE.ASCII_ONLY,
    }),

  last_name: z
    .string()
    .trim()
    .min(1, MESSAGE.REQUIRED("Last name"))
    .min(2, "Last name must be at least 2 characters")
    .regex(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY),

  phone_number: z
    .string()
    .min(1, MESSAGE.REQUIRED("Mobile Phone"))
    .refine((value) => isValidPhoneNumber(value), {
      message: "Must be a valid phone number",
    }),
});

export type SignerFormValues = z.infer<typeof schema>;

export type SignerFormProps = {
  profile: DTO.SavedProfile;
  signer: DTO.SavedProfileSigner;
  onSave: (values: SignerFormValues) => Promise<void>;
};

const getInitialValues = (signer: DTO.SavedProfileSigner): SignerFormValues => ({
  first_name: signer.first_name ?? "",
  middle_name: signer.middle_name ?? null,
  last_name: signer.last_name ?? "",
  phone_number: signer.phone_number ?? "",
});

const normalizeValues = (values: SignerFormValues): SignerFormValues => ({
  first_name: values.first_name.trim(),
  middle_name: values.middle_name?.trim() || null,
  last_name: values.last_name.trim(),
  phone_number: values.phone_number,
});

export function SignerForm({ profile, signer, onSave }: SignerFormProps) {
  const toast = useToast();

  const form = useForm<SignerFormValues>({
    defaultValues: getInitialValues(signer),
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const onSubmit = useCallback(
    async (values: SignerFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your signer details",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SmartFormProvider form={form}>
      {({ Input, PhoneField }) => (
        <SavedProfileFormContainer
          profile={profile}
          title="Signer Details"
          onSave={form.handleSubmit(onSubmit)}
          isLoading={form.formState.isSubmitting}
          isDisabled={!form.formState.isValid}>
          <Input name="first_name" label="First Name" isRequired />
          <Input name="middle_name" label="Middle Name" />
          <Input name="last_name" label="Last Name" isRequired />
          <PhoneField name="phone_number" label="Mobile Phone Number" isRequired />
        </SavedProfileFormContainer>
      )}
    </SmartFormProvider>
  );
}
